import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import useMembershipPlanService from '../../app/services/hooks/useMembershipPlanService';
import './Services.css';

// Importar imágenes
import checkListLegal from '../../assets/images/check_list_legal.png';
import registroMarca from '../../assets/images/registro_marca.png';
import redaccionContratos from '../../assets/images/redaccion_contratos.png';
import pactosAccionistas from '../../assets/images/pactos_accionistas.png';
import politicaPrivacidad from '../../assets/images/redaccion_politica_de_privacidad_de_datos_personales.png';

interface MembershipInfo {
    has_active_membership: boolean;
    can_use_contracts: boolean;
    can_use_chatbot: boolean;
    contracts_remaining: number;
    chatbot_messages_remaining: number;
    days_remaining: number;
    plan?: {
        name: string;
        price_formatted: string;
    };
}

interface Service {
    title: string;
    description: string;
    features?: string[];
    badges?: string[];
    image: string;
}

const Services = () => {
    const { auth } = useContext(AuthContext);
    const history = useHistory();
    const [membershipInfo, setMembershipInfo] = useState<MembershipInfo | null>(null);
    
    const { checkMembership, fetchingCheckMembership } = useMembershipPlanService();

    // Verificar si el usuario puede ver opciones de suscripción (no es admin)
    const canSubscribe = auth?.permissions?.includes('subscribe_usermembership') || 
                         (auth?.roles && !auth.roles.includes('Administrador'));
    
    // Si es administrador, no necesita ver nada de suscripción
    const isAdmin = auth?.roles?.includes('Administrador') || false;

    useEffect(() => {
        // Solo cargar info de membresía si no es admin
        if (!isAdmin) {
            loadMembershipInfo();
        }
    }, [isAdmin]);

    const loadMembershipInfo = () => {
        checkMembership({
            onSuccess: (res: any) => {
                if (res.data) {
                    setMembershipInfo(res.data);
                }
            },
            onError: () => {
                // Silently fail - user just doesn't have membership
            }
        });
    };
    
    const services: Service[] = [
        {
            title: "Registro de marca comercial",
            description: "El registro de una marca ofrece protección jurídica al titular, garantizándole el derecho exclusivo a utilizarla para identificar bienes o servicios por un período de 10 años renovables indefinidamente.",
            badges: [],
            image: registroMarca
        },
        {
            title: "Check-list legal",
            description: "El due diligence legal implica una investigación exhaustiva que se lleva a cabo previa a una inversión, adquisición o fusión de una empresa. A través de una lista de documentos cuidadosamente seleccionados, Monere te ayudará a prepararte para el proceso de manera eficiente.",
            badges: ["Gratuito"],
            image: checkListLegal
        },
        {
            title: "Redacción de contratos",
            description: "Podrás completar en línea los siguientes contratos:",
            features: [
                "Contrato de confidencialidad o NDA (Non Disclosure Agreement).",
                "Contrato de prestación de servicios.",
                "Contrato de trabajo.",
                "Finiquito laboral."
            ],
            badges: [],
            image: redaccionContratos
        },
        {
            title: "Pacto de accionistas",
            description: "El registro de una marca ofrece protección jurídica al titular, garantizándole el derecho exclusivo a utilizarla para identificar bienes o servicios por un período de 10 años renovables indefinidamente.",
            badges: [],
            image: pactosAccionistas
        },
        {
            title: "Redacción política de privacidad de datos personales",
            description: "Tiene por objetivo salvar la integridad y privacidad de los datos personales de los usuarios o clientes que entregan dicha información a tu empresa, en estricta observancia de la legislación y normativa vigente.",
            badges: [],
            image: politicaPrivacidad
        }
    ];

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="services-header mb-4">
                        <h1 className="services-title">¡Hola {auth?.firstName || 'Usuario'}!</h1>
                        <p className="services-subtitle">Gestiona y explora los servicios legales que tenemos para ofrecerte.</p>
                    </div>

                    {/* Banner de Suscripción - Solo para Cliente y Abogado, NO para Admin */}
                    {!isAdmin && !fetchingCheckMembership && (
                        membershipInfo?.has_active_membership ? (
                            <div className="subscription-banner active">
                                <div className="banner-icon"><i className="fas fa-check" /></div>
                            <div className="banner-content">
                                <h3>Plan {membershipInfo.plan?.name || 'Premium'} activo</h3>
                                <p>
                                    {[
                                        membershipInfo.contracts_remaining === -1 || membershipInfo.contracts_remaining > 999999
                                            ? 'Contratos ilimitados'
                                            : membershipInfo.contracts_remaining > 0 ? `${membershipInfo.contracts_remaining} contratos disponibles` : null,
                                        // Planes sin fecha de término (p.ej. los asignados por Monere) no muestran días
                                        membershipInfo.days_remaining >= 0 ? `${membershipInfo.days_remaining} días restantes` : null,
                                    ].filter(Boolean).join(' • ') || 'Plan vigente'}
                                </p>
                            </div>
                                <button 
                                    className="banner-button secondary"
                                    onClick={() => history.push('/intranet/my-subscription')}
                                >
                                    Ver mi plan
                                </button>
                            </div>
                        ) : (
                            <div className="subscription-banner promo">
                                <div className="banner-icon"><i className="fas fa-file-signature" /></div>
                                <div className="banner-content">
                                    <h3>Plan Monere Contracts + AI</h3>
                                    <p>
                                        <strong>Contratos ilimitados + Chatbot Legal 24/7</strong>
                                        <span className="price-tag">$39.990 + IVA / mes</span>
                                    </p>
                                </div>
                                <button 
                                    className="banner-button primary"
                                    onClick={() => history.push('/intranet/my-subscription')}
                                >
                                    Contratar ahora
                                </button>
                            </div>
                        )
                    )}

                    <div className="services-grid">
                        {services.map((service, index) => (
                            <div key={index} className="service-card">
                                <div className="service-card-header">
                                    <div className="service-icon">
                                        <img src={service.image} alt={service.title} className="service-image" />
                                    </div>
                                    {service.badges && service.badges.length > 0 && (
                                        <div className="service-badges">
                                            {service.badges.map((badge, badgeIndex) => (
                                                <span 
                                                    key={badgeIndex} 
                                                    className={`service-badge ${badge.toLowerCase()}`}
                                                >
                                                    {badge}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                <h3 className="service-card-title">{service.title}</h3>
                                
                                <p className="service-description">{service.description}</p>
                                
                                {service.features && service.features.length > 0 && (
                                    <ul className="service-features">
                                        {service.features.map((feature, featureIndex) => (
                                            <li key={featureIndex}>{feature}</li>
                                        ))}
                                    </ul>
                                )}

                                <button 
                                    className="service-button"
                                    onClick={() => {
                                        if (service.title === "Registro de marca comercial") {
                                            history.push('/intranet/brand-registration');
                                        } else if (service.title === "Check-list legal") {
                                            history.push('/intranet/checklists');
                                        }
                                    }}
                                >
                                    Iniciar
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Services;
